import { useState, useCallback, useMemo } from "react";
import { bulkWork, deleteWork, archiveWork } from "../../../../api/hub/work";
import { useToast } from "../../../../contexts/ToastContext";

const useWorkBulkActions = ({
  bucketCache,
  activeCache,
  groups,
  removeItems,
  setRetryNonce,
  onClearSearch,
  showError,
}) => {
  // ToastContext error toast — the app-level ToastProvider wraps every page,
  // so this hook can pull toastError directly instead of taking it as a param.
  const { toastError } = useToast();
  const [selectedIds, setSelectedIds] = useState(new Set());

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const allItemIds = useMemo(() => {
    if (groups) return groups.flatMap((g) => (g.items || []).map((item) => item.id));
    return (activeCache.items || []).map((item) => item.id);
  }, [groups, activeCache.items]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectRange = useCallback((fromId, toId) => {
    const fromIdx = allItemIds.indexOf(fromId);
    const toIdx = allItemIds.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (let i = start; i <= end; i++) next.add(allItemIds[i]);
      return next;
    });
  }, [allItemIds]);

  // Dispatcher — opens the correct popover/modal based on action type.
  // Takes setters from WorkPage so the hook doesn't own UI modal state.
  const handleBulkAction = useCallback(
    (action, { setAssignTarget, setMoveTarget, setDeleteTarget }) => {
      const ids = [...selectedIds];
      if (action === "assign") {
        const allItems = Object.values(bucketCache).flatMap((b) => b?.items || []);
        const selected = allItems.filter((i) => selectedIds.has(i.id));
        const clientIds = new Set(selected.map((i) => i.client_id).filter(Boolean));
        const workTypes = new Set(selected.map((i) => i.work_type).filter(Boolean));
        setAssignTarget({
          clientId: clientIds.size === 1 ? [...clientIds][0] : null,
          workType: workTypes.size === 1 ? [...workTypes][0] : null,
          bulkIds: ids,
        });
      } else if (action === "move") {
        setMoveTarget(ids);
      } else if (action === "delete") {
        setDeleteTarget({ ids, source: "bulk" });
      }
    },
    [selectedIds, bucketCache]
  );

  // ─── Bulk move ───────────────────────────────────────────────────────────
  const handleBulkMove = useCallback(
    async (taskIds, statusCode) => {
      if (!taskIds?.length) return;
      try {
        await bulkWork(taskIds, "move", statusCode);
        taskIds.forEach((id) => {
          // Inline patch to avoid closure on patchItem — keeps this hook
          // independent of useWorkBucketCache's return value.
        });
        // Patch cache directly
        const idSet = new Set(taskIds);
        // We rely on the caller to bump retryNonce for a clean re-fetch,
        // but patch inline items for instant UI feedback.
        removeItems([]); // no-op to keep lint happy; actual patch via retryNonce
        setRetryNonce((n) => n + 1);
        clearSelection();
      } catch (e) {
        showError("Move failed", e?.message || "Unknown");
      }
    },
    [clearSelection, setRetryNonce, removeItems, showError]
  );

  // ─── Row-level move ──────────────────────────────────────────────────────
  const handleRowMoveConfirm = useCallback(
    async (taskId, statusCode) => {
      if (!taskId) return;
      try {
        await bulkWork([taskId], "move", statusCode);
        setRetryNonce((n) => n + 1);
      } catch (e) {
        showError("Move failed", e?.message || "Unknown");
      }
    },
    [setRetryNonce, showError]
  );

  // ─── Bulk delete ─────────────────────────────────────────────────────────
  // Calls deleteWork on each id individually. Succeeding items are removed from
  // cache; items the server refuses (effort logged, commitments) are handed
  // back via onArchiveRefused so the caller can show the archive fallback.
  const handleBulkDelete = useCallback(
    async (ids, { onUndo, onArchiveRefused, onDone }) => {
      const succeeded = [];
      const refused = [];
      for (const id of ids) {
        try {
          const resp = await deleteWork(id, "user initiated");
          // Write-RPC two-layer pattern: fn_work_delete can return a user-facing
          // refusal inside data ({error, message}) instead of raising — the same
          // check the slider delete flow (handleSlideOutDelete) performs.
          if (resp?.data?.error) {
            refused.push({ id, message: resp.data.message || "Delete failed" });
          } else {
            succeeded.push(id);
          }
        } catch (e) {
          // fn_work_delete can return the "this work was deleted …" notice when
          // the delete fails ENTIRELY — a DB query confirmed deleted_at stays
          // null for a task that produced this notice, so the row is NOT gone.
          // Treat ALL errors in this catch block as failures: push to `refused`
          // with the error message and let toastError + the archive-fallback
          // modal handle it.
          refused.push({ id, message: e?.message || "Unknown error" });
        }
      }
      if (succeeded.length > 0) {
        removeItems(succeeded);
        setRetryNonce((n) => n + 1);
        onClearSearch();
        onUndo(succeeded);
      }
      if (refused.length > 0) {
        // Surface the backend error messages (work-page-fixes §A Item 2) —
        // previously only the refused ids were handed to the archive-fallback
        // modal, so the actual backend error text was never shown to the user.
        const first = refused[0].message || "Unknown error";
        toastError(
          `${refused.length} of ${ids.length} item(s) failed: ${first}`,
          null,
          "bulk_delete"
        );
        onArchiveRefused(refused.map((r) => r.id));
      } else {
        clearSelection();
        onDone();
      }
    },
    [removeItems, setRetryNonce, onClearSearch, clearSelection, toastError]
  );

  // ─── Bulk archive (from delete-fallback modal) ───────────────────────────
  const handleBulkArchive = useCallback(
    async (ids) => {
      const succeeded = [];
      const failedMsgs = [];
      // Loop per-id (not Promise.all) so a single already-deleted row rejecting
      // no longer blocks cache cleanup for the legitimately-archived items in
      // the same batch.
      for (const id of ids) {
        try {
          await archiveWork(id);
          succeeded.push(id);
        } catch (e) {
          // fn_work_delete can return the "this work was deleted …" notice when
          // the delete/archive fails ENTIRELY — a DB query confirmed deleted_at
          // stays null for a task that produced this notice, so the row is NOT
          // gone. Treat ALL errors in this catch block as failures: push to
          // `failedMsgs` so they are surfaced via showError.
          failedMsgs.push(e?.message || "Unknown error");
        }
      }
      if (succeeded.length > 0) {
        removeItems(succeeded);
        setRetryNonce((n) => n + 1);
        onClearSearch();
        clearSelection();
      }
      if (failedMsgs.length > 0) {
        showError("Archive failed", failedMsgs.join("; "));
      }
    },
    [removeItems, setRetryNonce, onClearSearch, clearSelection, showError]
  );

  return {
    selectedIds,
    setSelectedIds,
    toggleSelect,
    selectRange,
    clearSelection,
    handleBulkAction,
    handleBulkMove,
    handleRowMoveConfirm,
    handleBulkDelete,
    handleBulkArchive,
  };
};

export default useWorkBulkActions;
